import React, { useState, useEffect } from 'react';
import { walletService } from '../../services/wallet/walletService';
import { apiClient } from '../../services/api/apiClient';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (orderId: string, checkoutUrl: string) => void;
}

const PRESET_AMOUNTS = [10, 20, 50, 100, 250, 500];

const AddFundsModal: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const [amount, setAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gstRate, setGstRate] = useState<number>(0);

  useEffect(() => {
    if (!open) return;
    apiClient.get('/billing/gst/current')
      .then((res: any) => setGstRate(res.data?.rate_percentage ?? 0))
      .catch(() => setGstRate(0));
  }, [open]);

  if (!open) return null;

  const numAmount = parseFloat(amount) || 0;
  const gstAmount = numAmount > 0 ? numAmount * gstRate / 100 : 0;
  const totalAmount = numAmount + gstAmount;

  const handleAddFunds = async () => {
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await walletService.createRechargeOrder(numAmount, undefined, 'doctor_portal');
      if (result.checkout_url) {
        onSuccess(result.order_id, result.checkout_url);
      } else {
        setError('Checkout URL not provided by gateway.');
      }
    } catch (err: any) {
      const errorData = err?.response?.data?.detail;
      const errorMsg = typeof errorData === 'object' ? JSON.stringify(errorData) : errorData;
      setError(errorMsg || 'Failed to initiate recharge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="zivo-global-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="zivo-global-modal-content"
        role="dialog"
        aria-modal="true"
        aria-label="Add Funds"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '400px' }}
      >
        <div className="zivo-global-modal-header">
          <h3>Add Funds to Wallet</h3>
        </div>
        <div className="zivo-global-modal-body">
          <p style={{ marginBottom: '16px', color: '#64748b', fontSize: '14px' }}>
            Select or enter the amount you'd like to add to your credits (USD).
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
            {PRESET_AMOUNTS.map((amt) => (
              <button
                key={amt}
                type="button"
                className={`zivo-global-modal-button ${amount === amt.toString() ? '' : 'secondary'}`}
                style={{ 
                  padding: '8px', 
                  fontSize: '14px',
                  backgroundColor: amount === amt.toString() ? '#E30512' : '#f1f5f9',
                  color: amount === amt.toString() ? '#fff' : '#1e293b',
                  border: '1px solid ' + (amount === amt.toString() ? '#E30512' : '#e2e8f0'),
                }}
                onClick={() => setAmount(amt.toString())}
              >
                ${amt}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#94a3b8', marginBottom: '4px' }}>
              CUSTOM AMOUNT (USD)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 15.00"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '16px'
              }}
            />
          </div>

          {numAmount > 0 && (
            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              fontSize: '13px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#64748b' }}>
                <span>Base Amount</span>
                <span>${numAmount.toFixed(2)}</span>
              </div>
              {gstRate > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#64748b' }}>
                  <span>GST ({gstRate}%)</span>
                  <span>+${gstAmount.toFixed(2)}</span>
                </div>
              )}
              <div style={{ 
                display: 'flex', justifyContent: 'space-between',
                borderTop: '1px solid #e2e8f0', paddingTop: '6px',
                fontWeight: 700, color: '#1e293b',
              }}>
                <span>Total to Pay</span>
                <span>${totalAmount.toFixed(2)}</span>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '11px', marginTop: '6px', marginBottom: 0 }}>
                Your wallet will be credited <strong>${numAmount.toFixed(2)}</strong>.
                {gstRate > 0 && <span> GST of ${gstAmount.toFixed(2)} is charged as applicable by law.</span>}
              </p>
            </div>
          )}

          {error && (
            <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px' }}>
              {error}
            </div>
          )}
        </div>
        <div className="zivo-global-modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button 
            className="zivo-global-modal-button secondary" 
            onClick={onClose} 
            type="button"
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="zivo-global-modal-button" 
            onClick={handleAddFunds} 
            type="button"
            disabled={loading || !amount}
            style={{ backgroundColor: '#E30512', color: '#fff' }}
          >
            {loading ? 'Processing...' : `Pay $${totalAmount > 0 ? totalAmount.toFixed(2) : '0.00'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddFundsModal;
